import { Box, BoxProps, Flex, Grid, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import authApi from '~/authApi';
import IconButton from '~/components/IconButton';
import { SocialPlatformIcon } from '~/components/SocialPlatformIcon';
import { EditIcon } from '~/components/icons';
import { FEATURE_FLAGS, SOCIAL_PLATFORMS, USER_ROLES } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

interface Props extends BoxProps {
  title: string;
  onEdit: () => void;
  isSharedView?: boolean;
  platform: SOCIAL_PLATFORMS;
}

export const Layout: React.FC<Props> = ({
  title,
  onEdit = () => {},
  children,
  platform,
  isSharedView,
  ...props
}) => {
  const { isEnabled: isMediaPacksDeprecated } = useFeature(
    FEATURE_FLAGS.foamDeprecateMediaPacks,
  );
  const { isEnabled: isMediaKitsForCreatorsEnabled } = useFeature(
    FEATURE_FLAGS.foamCreatorMediaKits,
  );

  const canEditMediaPack = useMemo(() => {
    const userIsManager = authApi.user.roles.includes(USER_ROLES.talentManager);

    if (isSharedView) return false;

    if (userIsManager) {
      return !isMediaPacksDeprecated;
    }

    return !isMediaKitsForCreatorsEnabled;
  }, [isMediaPacksDeprecated, isMediaKitsForCreatorsEnabled, isSharedView]);

  return (
    <Box {...props}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          <SocialPlatformIcon platform={platform} boxSize={8} />
          <Text
            marginLeft={4}
            fontWeight="semibold"
            fontSize={{ base: '2xl', md: '3xl' }}
            as="span"
          >
            {title}
          </Text>
        </Flex>

        {canEditMediaPack && (
          <IconButton
            borderRadius="full"
            boxSize={10}
            _hover={{
              backgroundColor: 'primary.50',
            }}
            variant="ghost"
            aria-label="Edit metrics"
            icon={<EditIcon onClick={onEdit} />}
          />
        )}
      </Flex>
      <Grid
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
          xl: 'repeat(3, 1fr)',
        }}
        gridGap={{ base: 9, md: 4, lg: 6 }}
        width="100%"
        marginTop={10}
      >
        {children}
      </Grid>
    </Box>
  );
};
